// components/agent-chat/components/MessageBubble.jsx
import ReactMarkdown from 'react-markdown';
import AudioPlayer from './AudioPlayer';
import ImageViewer from './ImageViewer';
import VideoPlayer from './VideoPlayer'; // Importar o novo componente
import { useEffect, useState } from 'react';
import {
  ChevronDown, ChevronRight, Check, Copy, Wrench,
  Database, Zap, User
} from 'lucide-react';
import { getFileIcon } from './FileIcons';
import { getContrastColor } from '../../lib/colorUtils';

{/* Componente para visualização expandida de JSONs */}
const ExpandableJson = ({ data, title, initiallyExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const toggleExpand = () => setIsExpanded(!isExpanded);
  
  const copyToClipboard = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };
  
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden mt-1">
      <div 
        className="flex items-center justify-between bg-gray-50 px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={toggleExpand}
      >
        <div className="flex items-center">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500 mr-2" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500 mr-2" />
          )}
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
        <div className="flex items-center">
          <button
            onClick={copyToClipboard}
            className="p-1 hover:bg-gray-200 rounded text-gray-500"
            title="Copiar para área de transferência"
          >
            {copySuccess ? (
              <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="p-3 bg-gray-50 border-t border-gray-200 overflow-x-auto">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap">{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};


const MessageBubble = ({ agent, message, widgetSettings, formatTimestamp }) => {
  const isUserMessage = message.role === 'user';
  const isSystemMessage = message.role === 'system';
  const isHumanInLoop = message.metadata?.human_in_loop;

  // Estado para armazenar URLs de áudio validadas
  const [validatedAudioUrls, setValidatedAudioUrls] = useState([]);
  
  // Estado para armazenar URLs de imagens validadas
  const [validatedImageUrls, setValidatedImageUrls] = useState([]);

  // Estado para armazenar HTMLs
  const [htmlsRenders, setHtmlsRenders] = useState([]);

  // Estado para armazenar URLs de vídeos validadas
  const [validatedVideoUrls, setValidatedVideoUrls] = useState([]);

  // Estado para armazenar documentos validados
  const [validatedDocuments, setValidatedDocuments] = useState([]);
  
  // Validar as URLs de mídia e remover duplicatas
  useEffect(() => {
    if (!isUserMessage) {
      // Processar URLs de áudio
      if (message.audioUrls && message.audioUrls.length > 0) {
        const validUrls = message.audioUrls
          .filter(url => url && typeof url === 'string' && url.trim() !== '')
          .filter((url, index, self) => self.indexOf(url) === index);
        setValidatedAudioUrls(validUrls);
      } else {
        setValidatedAudioUrls([]);
      }
      
      // Processar URLs de imagens
      if (message.imageUrls && message.imageUrls.length > 0) {
        const validUrls = message.imageUrls
          .filter(url => url && typeof url === 'string' && url.trim() !== '')
          .filter((url, index, self) => self.indexOf(url) === index);
        setValidatedImageUrls(validUrls);
      } else {
        setValidatedImageUrls([]);
      }

      // Processar HTMLS
      if (message.htmlFiles && message.htmlFiles.length > 0) {
        setHtmlsRenders(message.htmlFiles);
      } else {
        setHtmlsRenders([]);
      }

      // Processar URLs de vídeos
      if (message.videoUrls && message.videoUrls.length > 0) {
        const validUrls = message.videoUrls
          .filter(url => url && typeof url === 'string' && url.trim() !== '')
          .filter((url, index, self) => self.indexOf(url) === index);
        setValidatedVideoUrls(validUrls);
      } else {
        setValidatedVideoUrls([]);
      }
    } else {
      setValidatedAudioUrls([]);
      setValidatedImageUrls([]);
      setValidatedVideoUrls([]);
    }

    // Processar documentos
    const docs = [];
    const videos = [];
    // Verificar se temos message.files (carregados a partir do histórico)
    if (message.files && Array.isArray(message.files)) {
      message.files.forEach(file => {
        // Verificar se é um vídeo pelo mime type
        if (file.mime_type && (
          file.mime_type.startsWith('video/') || 
          file.mime_type === 'video/mp4' || 
          file.mime_type === 'video/webm' || 
          file.mime_type === 'video/ogg'
        )) {
          videos.push(file.file_url);
        } else if(file.mime_type !== 'text/html') {
          docs.push(file);
        }
      });
    }
    // Adicionar vídeos dos files às URLs de vídeo validadas
    if (videos.length > 0) {
      setValidatedVideoUrls(prev => {
        const combined = [...prev, ...videos];
        // Remover duplicatas
        return combined.filter((url, index, self) => self.indexOf(url) === index);
      });
    }

    setValidatedDocuments(docs);
  }, [isUserMessage, message.audioUrls, message.imageUrls, message.videoUrls, message.files]);

  // Renderizar mensagens de sistema
  if (isSystemMessage) {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-full text-sm">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`max-w-[80%] ${
        isUserMessage 
          ? `agent-primary-bg text-white` 
          : (message.role === "tool" || (message.tool_calls && message.tool_calls.length > 0)) ? '' : `agent-message-bubble`
      } rounded-2xl px-4 py-3`}
      style={{ 
        fontFamily: widgetSettings.font_secondary || 'Roboto',
        minWidth: !isUserMessage ? '70%' : 'auto' 
      }}
    >
      {/* Indicador de Human in Loop */}

      {isHumanInLoop && !isUserMessage && (
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
          <div
            className="flex items-center gap-1 text-xs"
            style={{ color: getContrastColor(widgetSettings.secondary_color || '#E0E7FF') }}
          >
            <User className="w-3 h-3" />
            <span>Enviado manualmente pelo administrador</span>
          </div>
        </div>
      )}


      {/* Renderização para mensagens de usuário */}
      {isUserMessage ? (
        <div>
          <div className="mb-1">{message.content}</div>
          {validatedDocuments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {validatedDocuments.map((doc, index) => (
                <a
                  key={index}
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 rounded-lg p-3 transition-all ${
                    isUserMessage 
                      ? 'bg-gray-100/20 hover:bg-gray-100/30' 
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                  download
                >
                  <img 
                    src={getFileIcon(doc.mime_type)} 
                    alt="File icon" 
                    className="w-6 h-6 object-contain"
                  />
                  <span className={`text-sm truncate ${isUserMessage ? 'text-white/90' : 'text-gray-700'}`}>
                    {doc?.file_name || "Anexo"}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      ) : message.role !== "tool" && (
        // Renderização para mensagens do assistant com ReactMarkdown
        <div>
          <div className="markdown-content mb-1">
            <ReactMarkdown
              components={{
                img: ({ node, ...props }) => {
                  if (props.src && props.src.includes('oaidalleapiprodscus')) {
                    return null;
                  }
                  return <img className="max-w-full rounded my-2" {...props} alt={props.alt || 'imagem'} />;
                },
                a: ({ node, ...props }) => {
                  if (props.href && props.href.includes('oaidalleapiprodscus')) {
                    return null;
                  }
                  return <a className="text-blue-600 hover:underline" {...props} />;
                },
                p: ({ node, ...props }) => <p className="mb-4" {...props} />,
                h1: ({ node, ...props }) => <h1 className="text-xl font-bold mb-3 mt-4" {...props} />,
                h2: ({ node, ...props }) => <h2 className="text-lg font-bold mb-2 mt-3" {...props} />,
                h3: ({ node, ...props }) => <h3 className="text-md font-bold mb-2 mt-3" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc ml-5 mb-4" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal ml-5 mb-4" {...props} />,
                li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                blockquote: ({ node, ...props }) => (
                  <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2" {...props} />
                ),
                code: ({ node, inline, ...props }) => 
                  inline ? 
                    <code className="bg-gray-100 px-1 py-0.5 rounded" {...props} /> :
                    <code className="block bg-gray-100 p-2 rounded my-2 overflow-x-auto" {...props} />,
                pre: ({ node, ...props }) => <pre className="bg-gray-100 p-2 rounded my-2 overflow-x-auto" {...props} />
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>

          {/* Exibir reprodutores de vídeo */}
          {validatedVideoUrls && validatedVideoUrls.length > 0 && (
            <div className="mt-3 pt-2">
              {validatedVideoUrls.map((url, index) => (
                <VideoPlayer 
                  key={`video-${index}-${encodeURIComponent(url.substring(0, 20))}`}
                  url={url}
                  primaryColor={widgetSettings.primary_color}
                  title={`video-${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Exibir imagens anexadas */}
          {validatedImageUrls && validatedImageUrls.length > 0 && (
            <div className="mt-3 border-t border-gray-200 pt-2">
              {validatedImageUrls.map((url, index) => (
                <ImageViewer 
                  key={`image-${index}-${encodeURIComponent(url.substring(0, 20))}`}
                  url={url}
                  alt={`Imagem gerada ${index + 1}`}
                  primaryColor={widgetSettings.primary_color}
                />
              ))}
            </div>
          )}

          {/* Exibir HTMLs como botão de download */}
          {htmlsRenders && htmlsRenders.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {htmlsRenders.map((html, index) => {
                const blob = new Blob([html], { type: 'text/html' });
                const url = URL.createObjectURL(blob);

                return (
                  <a
                    key={index}
                    href={url}
                    download={`conteudo-${index + 1}.html`}
                    className="flex items-center gap-2 rounded-lg p-3 transition-all bg-gray-100 hover:bg-gray-200"
                  >
                    <img 
                      src={getFileIcon("text/html")} 
                      alt="HTML file" 
                      className="w-6 h-6 object-contain"
                    />
                    <span className="text-sm truncate text-gray-700">
                      Baixar HTML {index + 1}
                    </span>
                  </a>
                );
              })}
            </div>
          )}


          
          {/* Reprodutor de áudio com URLs validadas */}
          {validatedAudioUrls && validatedAudioUrls.length > 0 && (
            <div className="mt-3 border-t border-gray-200 pt-2">
              {validatedAudioUrls.map((url, index) => (
                <AudioPlayer 
                  key={`audio-${index}-${encodeURIComponent(url.substring(0, 20))}`}
                  url={url}
                  primaryColor={widgetSettings.primary_color}
                />
              ))}
            </div>
          )}

          {/* Exibir documentos anexados (para assistant) */}
          {validatedDocuments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {validatedDocuments.map((doc, index) => (
                <a
                  key={index}
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-lg p-3 transition-all bg-gray-100 hover:bg-gray-200"
                  download
                >
                  <img 
                    src={getFileIcon(doc.mime_type)} 
                    alt="File icon" 
                    className="w-6 h-6 object-contain"
                  />
                  <span className="text-sm truncate text-gray-700">
                    {doc?.file_name || "Anexo"}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Exibição de Tool Calls com estilo melhorado */}
      {!isUserMessage && agent?.user_id === JSON.parse(localStorage.getItem('userData'))?.id && message.tool_calls && message.tool_calls.length > 0 && (
        <div className="p-2 bg-blue-50 rounded-lg">
          <div className="flex items-center mb-2">
            <Wrench className="w-4 h-4 text-blue-600 mr-2" />
            <span className="text-sm font-medium text-blue-700">Ferramentas Utilizadas ({message.tool_calls.length})</span>
          </div>
          
          <div className="space-y-2">
            {message.tool_calls.map((toolCall, index) => {
              const functionName = toolCall?.function?.name || toolCall?.tool_call?.function?.name || 'Ferramenta';
              const functionArgs = toolCall?.function?.arguments || toolCall?.tool_call?.function?.arguments || {};
              
              let args = functionArgs;
              try {
                if (typeof functionArgs === 'string') {
                  args = JSON.parse(functionArgs);
                }
              } catch(e) {
                console.warn('Erro ao parsear argumentos da função:', e);
              }
              
              return (
                <div key={index} className="bg-white rounded-md overflow-hidden shadow-sm">
                  <div className="px-3 py-2 bg-blue-100 flex items-center justify-between">
                    <div className="flex items-center">
                      <Database className="w-4 h-4 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-700">{functionName}</span>
                    </div>
                    <div className="text-xs text-blue-600 font-mono bg-blue-50 px-2 py-0.5 rounded">
                      ID: {(toolCall.id || toolCall.tool_call_id || '').substring(0, 8)}
                    </div>
                  </div>
                  
                  <ExpandableJson 
                    data={args} 
                    title="Parâmetros" 
                    initiallyExpanded={false}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Exibição de Resultados de Ferramentas com estilo melhorado */}
      {!isUserMessage && agent?.user_id === JSON.parse(localStorage.getItem('userData'))?.id && (
        (message.role === "tool" || (message.tool_results && message.tool_results.length > 0))
      ) && (
        <div className="p-2 bg-green-50 rounded-lg">
          <div className="flex items-center mb-2">
            <Check className="w-4 h-4 text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-700">
              Resultados de Ferramentas
              {message.tool_results && ` (${message.tool_results.length})`}
            </span>
          </div>
          
          {message.role === "tool" ? (
            <div className="bg-white rounded-md overflow-hidden shadow-sm">
              <ExpandableJson 
                data={typeof message.content === 'string' ? { result: message.content } : message.content} 
                title="Resultado" 
                initiallyExpanded={false}
              />
            </div>
          ) : message.tool_results && message.tool_results.map((result, index) => {
            let content = result.content;
            try {
              if (typeof content === 'string') {
                content = JSON.parse(content);
              }
            } catch(e) {
              // Se não for um JSON válido, manter como string
            }
            
            return (
              <div key={index} className="bg-white rounded-md overflow-hidden shadow-sm mb-2 last:mb-0">
                <div className="px-3 py-2 bg-green-100 flex items-center justify-between">
                  <div className="flex items-center">
                    <Zap className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-700">
                      Resultado da ferramenta
                    </span>
                  </div>
                  <div className="text-xs text-green-600 font-mono bg-green-50 px-2 py-0.5 rounded">
                    ID: {(result.tool_call_id || '').substring(0, 8)}
                  </div>
                </div>
                
                <ExpandableJson 
                  data={content} 
                  title="Dados Retornados" 
                  initiallyExpanded={false}
                />
              </div>
            );
          })}
        </div>
      )}
      
      {/* Indicador de ferramenta em execução */}
      {!isUserMessage && message.executingTool && (
        <div className="animate-pulse mt-2 pt-2">
          <div className="mt-2 p-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded h-12 animate-shimmer overflow-hidden relative">
            <div className="text-md text-gray-600 absolute top-2 left-2 flex items-center">
              Pensando...
            </div>
          </div>
        </div>
      )}
      
      <div className={`text-xs ${
        isUserMessage 
          ? 'text-white text-opacity-80' 
          : 'text-gray-500'
      } text-right mt-2`}>
        {formatTimestamp(message.timestamp)}
      </div>
    </div>
  );
};

export default MessageBubble;